import { useId, type ReactNode } from 'react';

import { X } from 'lucide-react';

import { cx } from '@/lib/cx';
import { useOverlay } from '@/lib/useOverlay';

import { Button } from './Button';
import styles from './Modal.module.css';

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  tone?: 'primary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
};

export function Modal({
  open,
  onClose,
  title,
  description,
  icon,
  tone = 'primary',
  size = 'sm',
  children,
  footer,
  className,
}: ModalProps) {
  const titleId = useId();
  const descriptionId = useId();
  const ref = useOverlay(open, onClose);

  if (!open) return null;

  return (
    // Clicking the backdrop dismisses. The dialog itself stops the event, so a click
    // that starts inside and drifts out does not close it by accident.
    <div className={styles.scrim} onMouseDown={onClose}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cx(styles.dialog, styles[size], className)}
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
      >
        <div className={styles.head}>
          {icon ? (
            <span className={cx(styles.icon, tone === 'danger' && styles.iconDanger)}>
              {icon}
            </span>
          ) : null}
          <div className={styles.heading}>
            <h2 className={styles.title} id={titleId}>
              {title}
            </h2>
            {description ? (
              <p className={styles.description} id={descriptionId}>
                {description}
              </p>
            ) : null}
          </div>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <X size={17} />
          </button>
        </div>

        {children ? <div className={styles.body}>{children}</div> : null}
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </div>
    </div>
  );
}

export type ConfirmModalProps = {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
  danger?: boolean;
  busy?: boolean;
};

export function ConfirmModal({
  open,
  title,
  body,
  confirmLabel,
  onCancel,
  onConfirm,
  danger = false,
  busy = false,
}: ConfirmModalProps) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      description={body}
      tone={danger ? 'danger' : 'primary'}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}
